import React, { useEffect, useMemo, useRef, useState } from "react";

const CANVAS_WIDTH = 1000;
const CANVAS_HEIGHT = 700;

function createId(prefix = "mt") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function snapValue(value, step) {
  if (!step || step <= 0) return value;
  return Math.round(value / step) * step;
}

function normalizePoint(item, fallbackType = "attach") {
  return {
    id: item?.id || createId(fallbackType),
    name: item?.name || "",
    x: Number(item?.x) || 0,
    y: Number(item?.y) || 0,
    z: Number(item?.z) || 0,
    diameter: Number(item?.diameter) || 5,
    depth: Number(item?.depth) || 18,
    kind: item?.kind || fallbackType,
  };
}

export default function MountingSketchCanvas2D({
  plane = "xy",
  model,
  onChangeModel,
  selectedElement,
  onSelectElement,

  selectedDrillId = null,
  onSelectDrill,
}) {
  const svgRef = useRef(null);

  const [tool, setTool] = useState("select"); // select | pan | origin | attach | hole | cutout
  const [snapStep, setSnapStep] = useState(10);
  const [zoom, setZoom] = useState(0.8);
  const [pan, setPan] = useState({ x: 200, y: 520 });
  const [draggingKey, setDraggingKey] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [lastPointer, setLastPointer] = useState(null);

  const safeModel = model || {
  origin: { x: 0, y: 0, z: 0 },
  attachPoints: [],
  holes: [],
  cutouts: [],
  drillings: [],
};

  const attachPoints = useMemo(
    () => (safeModel.attachPoints || []).map((x) => normalizePoint(x, "attach")),
    [safeModel.attachPoints]
  );
  const holes = useMemo(
    () => (safeModel.holes || []).map((x) => normalizePoint(x, "hole")),
    [safeModel.holes]
  );
  const cutouts = useMemo(
    () => (safeModel.cutouts || []).map((x) => normalizePoint(x, "cutout")),
    [safeModel.cutouts]
  );

  const drillings = useMemo(
  () =>
    (safeModel.drillings || []).map((x) => ({
      ...normalizePoint(x, "drilling"),
      name: x?.name || "Drilling",
      kind: x?.kind || "blind",
      diameter: Number(x?.diameter) || 5,
      depth: Number(x?.depth) || 10,
      start: {
        x: Number(x?.start?.x) || 0,
        y: Number(x?.start?.y) || 0,
        z: Number(x?.start?.z) || 0,
      },
      axis: {
        x: Number(x?.axis?.x) || 0,
        y: Number(x?.axis?.y) || 0,
        z: Number(x?.axis?.z ?? 1) || 1,
      },
      color: x?.color || "#3b82f6",
      enabled: x?.enabled !== false,
    })),
  [safeModel.drillings]
);

  const items = useMemo(() => {
    const result = [];

    result.push({
      key: "origin",
      type: "origin",
      item: {
        id: "origin",
        name: "Origin",
        x: Number(safeModel.origin?.x) || 0,
        y: Number(safeModel.origin?.y) || 0,
        z: Number(safeModel.origin?.z) || 0,
      },
    });

    attachPoints.forEach((item) => result.push({ key: `attach:${item.id}`, type: "attach", item }));
    holes.forEach((item) => result.push({ key: `hole:${item.id}`, type: "hole", item }));
    cutouts.forEach((item) => result.push({ key: `cutout:${item.id}`, type: "cutout", item }));
    drillings.forEach((item) => result.push({ key: `drilling:${item.id}`, type: "drilling", item, })
);

    return result;
  }, [safeModel.origin, attachPoints, holes, cutouts, drillings]);

  function patchModel(nextPatch) {
    onChangeModel?.({
      ...safeModel,
      ...nextPatch,
    });
  }

  function getPlaneCoords(item) {
    if (plane === "xy") return { a: item.x, b: item.y };
    if (plane === "xz") return { a: item.x, b: item.z };
    return { a: item.y, b: item.z }; // yz
  }

  function patchPointByPlane(item, screenWorldA, screenWorldB) {
    if (plane === "xy") {
      return { ...item, x: screenWorldA, y: screenWorldB };
    }
    if (plane === "xz") {
      return { ...item, x: screenWorldA, z: screenWorldB };
    }
    return { ...item, y: screenWorldA, z: screenWorldB };
  }

  function worldToScreen(a, b) {
    return {
      x: pan.x + a * zoom,
      y: pan.y - b * zoom,
    };
  }

  function screenToWorld(x, y) {
    return {
      a: snapValue((x - pan.x) / zoom, snapStep),
      b: snapValue((pan.y - y) / zoom, snapStep),
    };
  }

  function getSvgPoint(e) {
    if (!svgRef.current) return null;
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  function setOriginFromWorld(a, b) {
    const current = {
      x: Number(safeModel.origin?.x) || 0,
      y: Number(safeModel.origin?.y) || 0,
      z: Number(safeModel.origin?.z) || 0,
    };

    const next = patchPointByPlane(current, a, b);
    patchModel({ origin: next });
    onSelectElement?.({ type: "origin", id: "origin" });
  }

  function addAttachPoint(a, b) {
    const item = patchPointByPlane(
      {
        id: createId("attach"),
        name: `Attach ${attachPoints.length + 1}`,
        x: 0,
        y: 0,
        z: 0,
        kind: "attach",
      },
      a,
      b
    );

    const next = [...attachPoints, item];
    patchModel({ attachPoints: next });
    onSelectElement?.({ type: "attach", id: item.id });
  }

  function addHole(a, b, kind = "screw") {
    const item = patchPointByPlane(
      {
        id: createId("hole"),
        name: `Hole ${holes.length + 1}`,
        x: 0,
        y: 0,
        z: 0,
        diameter: 5,
        depth: 18,
        kind,
      },
      a,
      b
    );

    const next = [...holes, item];
    patchModel({ holes: next });
    onSelectElement?.({ type: "hole", id: item.id });
  }

  function addCutout(a, b, kind = "cutout") {
    const item = patchPointByPlane(
      {
        id: createId("cutout"),
        name: `Cutout ${cutouts.length + 1}`,
        x: 0,
        y: 0,
        z: 0,
        diameter: 35,
        depth: 12,
        kind,
      },
      a,
      b
    );

    const next = [...cutouts, item];
    patchModel({ cutouts: next });
    onSelectElement?.({ type: "cutout", id: item.id });
  }

  function handleBackgroundClick(e) {
    const p = getSvgPoint(e);
    if (!p) return;

    const world = screenToWorld(p.x, p.y);

    if (tool === "origin") {
      setOriginFromWorld(world.a, world.b);
      return;
    }

    if (tool === "attach") {
      addAttachPoint(world.a, world.b);
      return;
    }

    if (tool === "hole") {
      addHole(world.a, world.b, "screw");
      return;
    }

    if (tool === "cutout") {
      addCutout(world.a, world.b, "cutout");
      return;
    }
  }

  function handleItemPointerDown(e, key, type, item) {
    e.stopPropagation();

    onSelectElement?.({
      type,
      id: item.id,
    });

    if (tool === "select") {
      setDraggingKey(key);
    }
  }

  function handlePointerDownBackground(e) {
    const p = getSvgPoint(e);
    setLastPointer(p);

    if (tool === "pan") {
      setIsPanning(true);
      return;
    }

    if (tool === "select") {
      onSelectElement?.(null);
    }
  }

  function updateDraggedItem(key, a, b) {
    if (key === "origin") {
      setOriginFromWorld(a, b);
      return;
    }

    const [type, id] = key.split(":");

    if (type === "attach") {
      patchModel({
        attachPoints: attachPoints.map((item) =>
          item.id === id ? patchPointByPlane(item, a, b) : item
        ),
      });
      return;
    }

    if (type === "hole") {
      patchModel({
        holes: holes.map((item) =>
          item.id === id ? patchPointByPlane(item, a, b) : item
        ),
      });
      return;
    }

    if (type === "cutout") {
      patchModel({
        cutouts: cutouts.map((item) =>
          item.id === id ? patchPointByPlane(item, a, b) : item
        ),
      });
    }

    if (type === "drilling") {
  patchModel({
    drillings: drillings.map((item) => {
      if (item.id !== id) return item;

      const nextStart = patchPointByPlane(item.start || {}, a, b);

      return {
        ...item,
        start: {
          ...(item.start || {}),
          x: nextStart.x,
          y: nextStart.y,
          z: nextStart.z,
        },
      };
    }),
  });

  onSelectDrill?.(id);
}
  }

  function handlePointerMove(e) {
    const p = getSvgPoint(e);
    if (!p) return;

    if (draggingKey) {
      const world = screenToWorld(p.x, p.y);
      updateDraggedItem(draggingKey, world.a, world.b);
      return;
    }

    if (isPanning && lastPointer) {
      const dx = p.x - lastPointer.x;
      const dy = p.y - lastPointer.y;

      setPan((prev) => ({
        x: prev.x + dx,
        y: prev.y + dy,
      }));

      setLastPointer(p);
    }
  }

  function handlePointerUp() {
    setDraggingKey(null);
    setIsPanning(false);
    setLastPointer(null);
  }

  function deleteSelected() {
    if (!selectedElement) return;
    if (selectedElement.type === "origin") return;

    if (selectedElement.type === "attach") {
      patchModel({
        attachPoints: attachPoints.filter((item) => item.id !== selectedElement.id),
      });
      onSelectElement?.(null);
      return;
    }

    if (selectedElement.type === "hole") {
      patchModel({
        holes: holes.filter((item) => item.id !== selectedElement.id),
      });
      onSelectElement?.(null);
      return;
    }

    if (selectedElement.type === "cutout") {
      patchModel({
        cutouts: cutouts.filter((item) => item.id !== selectedElement.id),
      });
      onSelectElement?.(null);
    }

    if (selectedElement.type === "drilling") {
  patchModel({
    drillings: drillings.filter((item) => item.id !== selectedElement.id),
  });
  onSelectElement?.(null);
  onSelectDrill?.(null);
}
  }

  function moveSelected(dx, dy) {
    if (!selectedElement) return;

    const current = items.find((x) => {
      if (selectedElement.type === "origin") return x.key === "origin";
      return x.type === selectedElement.type && x.item.id === selectedElement.id;
    });

    if (!current) return;

    const coords = getPlaneCoords(current.item);
    updateDraggedItem(
      current.key,
      snapValue(coords.a + dx, snapStep),
      snapValue(coords.b + dy, snapStep)
    );
  }

  function zoomBy(factor) {
    setZoom((prev) => Math.max(0.1, Math.min(5, prev * factor)));
  }

  function fitToContent() {
    const all = items.map((entry) => getPlaneCoords(entry.item));
    if (!all.length) return;

    const xs = all.map((p) => p.a);
    const ys = all.map((p) => p.b);

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const width = Math.max(maxX - minX, 100);
    const height = Math.max(maxY - minY, 100);

    const zoomX = (CANVAS_WIDTH - 120) / width;
    const zoomY = (CANVAS_HEIGHT - 120) / height;
    const nextZoom = Math.max(0.1, Math.min(5, Math.min(zoomX, zoomY)));

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    setZoom(nextZoom);
    setPan({
      x: CANVAS_WIDTH / 2 - centerX * nextZoom,
      y: CANVAS_HEIGHT / 2 + centerY * nextZoom,
    });
  }

  useEffect(() => {
    function onKeyDown(e) {
      if (!selectedElement) return;

      const step = e.shiftKey ? snapStep * 10 : snapStep;

      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        deleteSelected();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        moveSelected(-step, 0);
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        moveSelected(step, 0);
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        moveSelected(0, step);
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        moveSelected(0, -step);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedElement, items, snapStep]);

  const gridMinor = 10;
  const gridMajor = 50;
  const gridLines = [];

  const visibleWorldLeft = (-pan.x) / zoom;
  const visibleWorldRight = (CANVAS_WIDTH - pan.x) / zoom;
  const visibleWorldTop = pan.y / zoom;
  const visibleWorldBottom = (pan.y - CANVAS_HEIGHT) / zoom;

  const startX = Math.floor(visibleWorldLeft / gridMinor) * gridMinor;
  const endX = Math.ceil(visibleWorldRight / gridMinor) * gridMinor;
  const startY = Math.floor(visibleWorldBottom / gridMinor) * gridMinor;
  const endY = Math.ceil(visibleWorldTop / gridMinor) * gridMinor;

  for (let x = startX; x <= endX; x += gridMinor) {
    const sx = pan.x + x * zoom;
    gridLines.push(
      <line
        key={`vx_${x}`}
        x1={sx}
        y1={0}
        x2={sx}
        y2={CANVAS_HEIGHT}
        stroke={x % gridMajor === 0 ? "#94a3b8" : "#e5e7eb"}
        strokeWidth={x % gridMajor === 0 ? 1.2 : 0.6}
      />
    );
  }

  for (let y = startY; y <= endY; y += gridMinor) {
    const sy = pan.y - y * zoom;
    gridLines.push(
      <line
        key={`hy_${y}`}
        x1={0}
        y1={sy}
        x2={CANVAS_WIDTH}
        y2={sy}
        stroke={y % gridMajor === 0 ? "#94a3b8" : "#e5e7eb"}
        strokeWidth={y % gridMajor === 0 ? 1.2 : 0.6}
      />
    );
  }

  const originScreen = worldToScreen(0, 0);

  function getElementColor(type, selected) {
    if (selected) return "#f59e0b";
    if (type === "origin") return "#dc2626";
    if (type === "attach") return "#2563eb";
    if (type === "hole") return "#16a34a";
    if (type === "cutout") return "#7c3aed";
if (type === "drilling") return "#3b82f6";
return "#111827";
  }

  return (
    <div className="p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setTool("select")}
          className={`px-3 py-2 rounded border ${tool === "select" ? "bg-blue-600 text-white" : ""}`}
        >
          Select
        </button>
        <button
          onClick={() => setTool("origin")}
          className={`px-3 py-2 rounded border ${tool === "origin" ? "bg-blue-600 text-white" : ""}`}
        >
          Origin
        </button>
        <button
          onClick={() => setTool("attach")}
          className={`px-3 py-2 rounded border ${tool === "attach" ? "bg-blue-600 text-white" : ""}`}
        >
          Add Attach
        </button>
        <button
          onClick={() => setTool("hole")}
          className={`px-3 py-2 rounded border ${tool === "hole" ? "bg-blue-600 text-white" : ""}`}
        >
          Add Hole
        </button>
        <button
          onClick={() => setTool("cutout")}
          className={`px-3 py-2 rounded border ${tool === "cutout" ? "bg-blue-600 text-white" : ""}`}
        >
          Add Cutout
        </button>
        <button
          onClick={() => setTool("pan")}
          className={`px-3 py-2 rounded border ${tool === "pan" ? "bg-blue-600 text-white" : ""}`}
        >
          Pan
        </button>

        <div className="ml-4 flex items-center gap-2">
          <span className="text-sm">Plane</span>
          <span className="px-2 py-2 border rounded bg-gray-50 uppercase">{plane}</span>
        </div>

        <div className="ml-4 flex items-center gap-2">
          <span className="text-sm">Snap</span>
          <select
            value={snapStep}
            onChange={(e) => setSnapStep(Number(e.target.value))}
            className="px-2 py-2 border rounded"
          >
            <option value={1}>1 mm</option>
            <option value={5}>5 mm</option>
            <option value={10}>10 mm</option>
            <option value={25}>25 mm</option>
            <option value={50}>50 mm</option>
          </select>
        </div>

        <div className="ml-4 flex items-center gap-2">
          <button onClick={() => zoomBy(1.2)} className="px-3 py-2 rounded border">
            Zoom +
          </button>
          <button onClick={() => zoomBy(1 / 1.2)} className="px-3 py-2 rounded border">
            Zoom -
          </button>
          <button onClick={fitToContent} className="px-3 py-2 rounded border">
            Fit
          </button>
        </div>

        <div className="ml-auto text-xs text-gray-500">
          Origin / attach / holes / cutouts
        </div>
      </div>

      <div className="border rounded bg-white overflow-hidden">
        <svg
          ref={svgRef}
          width="100%"
          viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
          className={`w-full h-[700px] block ${
            tool === "pan"
              ? "cursor-grab"
              : tool === "select"
              ? "cursor-default"
              : "cursor-crosshair"
          }`}
          onClick={handleBackgroundClick}
          onPointerDown={handlePointerDownBackground}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          {gridLines}

          <line
            x1={0}
            y1={originScreen.y}
            x2={CANVAS_WIDTH}
            y2={originScreen.y}
            stroke="#16a34a"
            strokeWidth={1.5}
          />
          <line
            x1={originScreen.x}
            y1={0}
            x2={originScreen.x}
            y2={CANVAS_HEIGHT}
            stroke="#dc2626"
            strokeWidth={1.5}
          />

          {items.map((entry) => {
            const coords = getPlaneCoords(entry.item);
            const s = worldToScreen(coords.a, coords.b);
            const isSelected =
              selectedElement &&
              selectedElement.type === entry.type &&
              selectedElement.id === entry.item.id;

            const color = getElementColor(entry.type, isSelected);

            if (entry.type === "origin") {
              return (
                <g key={entry.key}>
                  <circle
                    cx={s.x}
                    cy={s.y}
                    r={9}
                    fill={color}
                    stroke="white"
                    strokeWidth={2}
                    onPointerDown={(e) => handleItemPointerDown(e, entry.key, entry.type, entry.item)}
                  />
                  <text x={s.x + 12} y={s.y - 12} fontSize="12" fill="#111827">
                    Origin ({entry.item.x}, {entry.item.y}, {entry.item.z})
                  </text>
                </g>
              );
            }

            const r =
  entry.type === "hole" || entry.type === "cutout" || entry.type === "drilling"
    ? Math.max(6, ((entry.item.diameter || 5) * zoom) / 2)
    : 7;

    if (entry.type === "drilling") {
  const start = getPlaneCoords(entry.item.start || {});
  const axisPoint = getPlaneCoords(entry.item.axis || {});

  const depth = Number(entry.item.depth || 10);

  const startScreen = worldToScreen(start.a, start.b);
  const endScreen = worldToScreen(
    start.a + axisPoint.a * depth,
    start.b + axisPoint.b * depth
  );

  const isDrillSelected =
    String(selectedDrillId) === String(entry.item.id) ||
    (selectedElement &&
      selectedElement.type === "drilling" &&
      String(selectedElement.id) === String(entry.item.id));

  const drillColor = isDrillSelected
    ? "#f59e0b"
    : entry.item.color || "#3b82f6";

  return (
    <g key={entry.key}>
      <circle
        cx={startScreen.x}
        cy={startScreen.y}
        r={Math.min(r, 30)}
        fill={drillColor}
        fillOpacity={0.25}
        stroke={drillColor}
        strokeWidth={isDrillSelected ? 3 : 1.5}
        onPointerDown={(e) => {
          handleItemPointerDown(e, entry.key, entry.type, entry.item);
          onSelectDrill?.(entry.item.id);
        }}
        onClick={(e) => {
          e.stopPropagation();
          onSelectElement?.({ type: "drilling", id: entry.item.id });
          onSelectDrill?.(entry.item.id);
        }}
        style={{ cursor: "pointer" }}
      />

      <line
        x1={startScreen.x}
        y1={startScreen.y}
        x2={endScreen.x}
        y2={endScreen.y}
        stroke={drillColor}
        strokeWidth={2}
      />

      <circle
        cx={endScreen.x}
        cy={endScreen.y}
        r={4}
        fill={drillColor}
      />

      <text
        x={startScreen.x + 10}
        y={startScreen.y - 10}
        fontSize="12"
        fill="#111827"
      >
        {entry.item.name || "Drilling"} Ø{entry.item.diameter} d{entry.item.depth}
      </text>
    </g>
  );
}

            return (
              <g key={entry.key}>
                <circle
                  cx={s.x}
                  cy={s.y}
                  r={Math.min(r, 20)}
                  fill={color}
                  fillOpacity={entry.type === "cutout" ? 0.25 : 1}
                  stroke={color}
                  strokeWidth={2}
                  onPointerDown={(e) => handleItemPointerDown(e, entry.key, entry.type, entry.item)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectElement?.({ type: entry.type, id: entry.item.id });
                  }}
                  style={{ cursor: "pointer" }}
                />
                <text x={s.x + 10} y={s.y - 10} fontSize="12" fill="#111827">
                  {entry.item.name || entry.type} ({entry.item.x}, {entry.item.y}, {entry.item.z})
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="flex gap-2">
        <button onClick={() => moveSelected(-snapStep, 0)} className="px-3 py-2 rounded border">
          ←
        </button>
        <button onClick={() => moveSelected(snapStep, 0)} className="px-3 py-2 rounded border">
          →
        </button>
        <button onClick={() => moveSelected(0, snapStep)} className="px-3 py-2 rounded border">
          ↑
        </button>
        <button onClick={() => moveSelected(0, -snapStep)} className="px-3 py-2 rounded border">
          ↓
        </button>
        <button onClick={deleteSelected} className="px-3 py-2 rounded border text-red-600">
          Delete Selected
        </button>
      </div>
    </div>
  );
}